import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Eye,
  Settings,
  MoreVertical,
  Users,
  Activity,
  Edit3,
  Trash2
} from "lucide-react";
import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { EditTeamModal } from '@/components/modals/EditTeamModal';
import { DeleteTeamModal } from '@/components/modals/DeleteTeamModal';

interface TeamMember {
  id: string;
  name: string;
  color?: string;
  title?: string;
  avatar?: string;
}

interface EnhancedTeamCardProps {
  team: {
    id: string;
    name: string;
    description: string;
    icon: LucideIcon;
    members: number;
    lead: string;
    performance: number;
    memberAvatars: TeamMember[];
    additionalMembers: number;
  };
  className?: string;
}

export function EnhancedTeamCard({ team, className = "" }: EnhancedTeamCardProps) {
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);

  const IconComponent = team.icon;
  const performanceLevel = team.performance >= 80 ? 'high' : team.performance >= 60 ? 'medium' : 'low';

  const performanceConfig = {
    high: { color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' },
    medium: { color: 'text-primary', bg: 'bg-primary/10', border: 'border-primary/20' },
    low: { color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' }
  };

  const handleEditSuccess = () => {
    // The useUpdateTeam hook should automatically invalidate queries and refetch
    setIsEditModalOpen(false);
  };

  const handleDeleteSuccess = () => {
    // The useDeleteTeam hook should automatically invalidate queries and refetch
    setIsDeleteModalOpen(false);
  };

  return (
    <div className={cn(
      "group relative bg-card border border-border hover:border-border/80 rounded-2xl p-6",
      "shadow-sm hover:shadow-lg transition-all duration-300 ease-out hover:-translate-y-1",
      "overflow-hidden",
      className
    )}>
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-primary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/20 border border-primary/20 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform duration-200 shadow-sm">
              <IconComponent className="h-6 w-6 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                {team.name}
              </h3>
              <p className="text-sm text-muted-foreground truncate leading-tight">
                {team.description}
              </p>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 h-8 w-8 p-0 rounded-lg hover:bg-muted"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setIsEditModalOpen(true)}>
                <Edit3 className="h-4 w-4 mr-2" />
                Edit Team
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setIsDeleteModalOpen(true)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Team
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Team Members */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="flex items-center -space-x-2">
              {team.memberAvatars.map((avatar, index) => (
                <Avatar
                  key={avatar.id}
                  className={cn(
                    "h-8 w-8 border-2 border-card shadow-sm transition-transform duration-200 cursor-pointer",
                    "bg-muted",
                    index > 0 && "hover:-translate-x-1",
                    "hover:scale-110 hover:z-10"
                  )}
                  title={avatar.title || `Member ${index + 1}`}
                >
                  <AvatarImage src={avatar.avatar || undefined} alt={avatar.title || avatar.name} />
                  <AvatarFallback className={cn('text-xs font-medium', avatar.color || 'bg-muted text-muted-foreground')}>
                    {avatar.name}
                  </AvatarFallback>
                </Avatar>
              ))}
              {team.additionalMembers > 0 && (
                <div className="w-8 h-8 bg-gradient-to-br from-primary/20 to-primary/30 border-2 border-card rounded-full flex items-center justify-center text-xs font-medium text-primary shadow-sm hover:scale-110 transition-transform duration-200">
                  +{team.additionalMembers}
                </div>
              )}
            </div>
          </div>

          <div className="text-right">
            <div className="text-sm font-semibold text-foreground flex items-center gap-1">
              <Users className="h-4 w-4 text-muted-foreground" />
              {team.members}
            </div>
            <div className="text-xs text-muted-foreground">{team.lead}</div>
          </div>
        </div>

        {/* Performance */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Team Performance
            </span>
            <Badge className={cn(
              "text-xs font-medium border",
              performanceConfig[performanceLevel].color,
              performanceConfig[performanceLevel].bg,
              performanceConfig[performanceLevel].border
            )}>
              {team.performance}%
            </Badge>
          </div>

          <div className="relative">
            <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
              <div
                className={cn(
                  "h-full rounded-full transition-all duration-700 ease-out",
                  performanceLevel === 'high' && "bg-gradient-to-r from-emerald-400 to-emerald-500",
                  performanceLevel === 'medium' && "bg-gradient-to-r from-blue-400 to-blue-500",
                  performanceLevel === 'low' && "bg-gradient-to-r from-amber-400 to-amber-500"
                )}
                style={{ width: `${team.performance}%` }}
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Link to={`/team/${team.id}`} className="flex-1">
            <Button
              className="w-full gap-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg font-medium transition-all duration-200 hover:shadow-md"
              size="sm"
            >
              <Eye className="h-4 w-4" />
              View Team
            </Button>
          </Link>
          <Link to={`/team/${team.id}/settings`}>
            <Button
              variant="outline"
              size="sm"
              className="rounded-lg border-border hover:border-border/80 hover:bg-muted transition-all duration-200"
            >
              <Settings className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Hover accent */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 to-purple-500 rounded-b-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      {/* Modals */}
      <EditTeamModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        team={team}
        onSuccess={handleEditSuccess}
      />

      <DeleteTeamModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        team={team}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  );
}
